import memes from '../../../memes';
import { RowPart, Seat } from '../../types';
import { Money } from 'types';
import { SelectedSeatsServiceType } from '../../../Checkout/store/selectedServices/reducers';
import {
	FlightSeatMapRowFragment,
	FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment,
	RowSeatFragment,
	SeatComfort
} from '@websky/graphql';
import { getSeatChangeChargeServicesForPassenger, getSeatPrice } from '../../utils';
import { GdsServiceById } from '@shared/lib/gdsService';

export type RowWithParts = FlightSeatMapRowFragment & {
	parts: RowPart[];
};

const isSelectedSeatGuard = (
	seat: Seat | Partial<SelectedSeatsServiceType>
): seat is Partial<SelectedSeatsServiceType> => {
	return seat.hasOwnProperty('type') || seat.hasOwnProperty('deckId') || seat.hasOwnProperty('isConfirmed');
};

export const isFreeSeat = (seat: Seat | Partial<SelectedSeatsServiceType>, passengerId?: string): boolean => {
	const seatPrice = getSeatPrice(seat, passengerId);

	if (isSelectedSeatGuard(seat)) {
		return !seat.rfisc && seatPrice?.amount === 0;
	} else {
		return seatPrice?.amount === 0 && !seat.rfisc && seat.isAvailable && seat.isExistent;
	}
};

export const isUnavailableSeat = (seat: Seat): boolean => {
	// Если нет rfisc и нет сервиса, но место доступно, то считается бесплатным местом
	if (!seat.rfisc && !seat.seatService && seat.isExistent && seat.isAvailable) {
		return false;
	}

	// Если сервис не может быть добавлен, то считается недоступным местом
	if (seat.seatService && !seat.seatService.canBeAdded) {
		return true;
	}

	return !seat.isExistent || !seat.isAvailable;
};

export const hasSeatChangeChargeService = (seat: Seat, gdsServiceById: GdsServiceById, passengerId: string): boolean =>
	getSeatChangeChargeServicesForPassenger(seat, gdsServiceById, passengerId).length > 0;

export const getRowService = memes(
	(seats: RowSeatFragment[]): RowSeatFragment['seatService'] =>
		seats[0].isExistent && !seats[0].isAisle && seats[0].seatService
);

export const cabinCodeComfortMap = new Map<string, SeatComfort>([
	['C', SeatComfort.Comfort],
	['Y', SeatComfort.Standard]
]);

const getRowComfort = (row: RowWithParts): SeatComfort => {
	if (!row?.seats?.length) {
		return null;
	}

	const comfortCountMap = new Map<SeatComfort, number>();

	// Collect the count of available comfort levels by rows
	// to return the most frequently occurring comfort
	row.seats.forEach(seat => {
		if (seat.seatService?.comfort && !seat.isAisle) {
			const comfort = seat.seatService.comfort;

			comfortCountMap.set(comfort, (comfortCountMap.get(comfort) ?? 0) + (seat.isAvailable ? 1 : 0));
		}
	});

	if (comfortCountMap.size > 1) {
		// Returns the most frequently occurring comfort
		return [...comfortCountMap.entries()].sort(([_a, aCount], [_b, bCount]) => bCount - aCount)[0][0];
	}

	return [...comfortCountMap.keys()][0];
};

export const getRowsWithDifferentClasses = memes(
	(
		rows: RowWithParts[],
		splitByCabin?: boolean,
		isNewServicePredicate?: (row: RowWithParts, nextRow: RowWithParts) => boolean
	) => {
		const rowsWithDifferentClassesMap = new Map<number, SeatComfort>();

		const rowComfort = getRowComfort(rows[0]);

		if (rowComfort) {
			rowsWithDifferentClassesMap.set(rows[0].number, rowComfort);
		}

		rows.forEach((nextRow, index) => {
			const prevRow = rows[index - 1];

			if (prevRow) {
				const lastRowNumber = [...rowsWithDifferentClassesMap.keys()].pop();
				const lastRowComfort = rowsWithDifferentClassesMap.get(lastRowNumber);

				const prevRowComfort = getRowComfort(prevRow) ?? lastRowComfort;
				const rowComfort = getRowComfort(nextRow) ?? lastRowComfort;

				const diffComfort = prevRowComfort !== rowComfort;

				if (isNewServicePredicate) {
					if (isNewServicePredicate(prevRow, nextRow)) {
						rowsWithDifferentClassesMap.set(nextRow.number, rowComfort);
					}
				} else {
					if (splitByCabin) {
						const diffCabinCode = prevRow.cabinCode !== nextRow.cabinCode;

						if (diffCabinCode) {
							rowsWithDifferentClassesMap.set(nextRow.number, cabinCodeComfortMap.get(nextRow.cabinCode));
						}
					} else if (diffComfort) {
						rowsWithDifferentClassesMap.set(nextRow.number, rowComfort);
					}
				}
			}
		});

		return rowsWithDifferentClassesMap;
	}
);

export const getMinPriceOfServiceClass = (
	rows: RowWithParts[],
	firstIdxOfNewClass: number,
	comfort: string,
	rowsWithDifferentClasses: Map<number, SeatComfort>
): Money => {
	let minPrice: Money = null;

	let rowIdx = firstIdxOfNewClass;
	let isNextClass = false;

	while (rowIdx < rows.length && !isNextClass) {
		const row = rows[rowIdx];

		row.parts.forEach(part => {
			part.seats.forEach(seat => {
				if (seat.isAvailable && seat.price) {
					if (!minPrice && seat.price) {
						minPrice = seat.price;
					}

					if (seat.price.amount < minPrice?.amount) {
						minPrice = seat.price;
					}
				}
			});
		});

		rowIdx++;
		isNextClass = rowsWithDifferentClasses.has(rows[rowIdx]?.number);
	}

	return minPrice;
};

export const getActualService = (
	services: {
		[serviceId: string]: FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment;
	},
	part: RowPart
): SeatComfort => {
	// пробуем найти по максимальному количеству однотипных сервисов
	const serviceComfortCount: Map<string, { serviceId: string; count: number }> = new Map();

	for (const seat of part.seats.values()) {
		if (seat.seatService) {
			let countItem = serviceComfortCount.get(seat.seatService.comfort);
			if (!countItem) {
				countItem = { serviceId: seat.seatService.id, count: 0 };
				serviceComfortCount.set(seat.seatService.comfort, countItem);
			}
			countItem.count++;
		}
	}

	let maxCount = 0;
	let serviceId: string = null;
	serviceComfortCount.forEach(item => {
		if (item.count > maxCount) {
			maxCount = item.count;
			serviceId = item.serviceId;
		}
	});

	if (serviceId) {
		if (services?.hasOwnProperty(serviceId)) {
			return services[serviceId]?.comfort;
		} else {
			return part.seats.find(seat => seat.seatService?.id === serviceId)?.seatService?.comfort;
		}
	}

	// тогда по первому существующему
	const firstExistService = part.seats.find(seat => seat.seatService);
	if (firstExistService?.seatService) {
		return services[firstExistService.seatService.id]?.comfort;
	}

	return null;
};
